import type { ReactNode } from "react";
import { Send } from "lucide-react";
import type { PrequalInviteDto } from "@/api/buyers";
import { Button, SubTable, Surface, useToast } from "@/design-system/components/ui";
import { DetailSection } from "../detailBits";
import {
  DuplicateInviteConfirm,
  InviteListState,
  copyInviteLink,
  inviteColumns,
} from "./inviteBits";
import { PREQUAL_SUBJECT_COPY, type PrequalSubject } from "./prequalSubject";

/**
 * Every prequalification invitation this record has sent — one section, both
 * subjects.
 *
 * <p>The columns, the duplicate confirm, the clipboard behaviour and the
 * loading/failed scaffold already came from <c>inviteBits</c>. This is the
 * SCAFFOLD around them — the card, the compose slot, where the duplicate confirm
 * sits relative to the form, and the table — which shipped twice and drifted on
 * nothing but a noun.</p>
 *
 * <p>🔴 <b>Carded, like every other section on the records this mounts on.</b>
 * The whole prequalification surface rendered as a bare stack directly on the
 * page background while its own read-only twin on Overview
 * (<c>PrequalificationLinkPanel</c>) was a <c>DetailSection</c> — the same facts,
 * two chrome vocabularies, on two tabs of one record.</p>
 *
 * <p>🔴 <b>What is deliberately NOT shared is the composition step, and it is a
 * real difference rather than an oversight.</b> A buyer's address is on their own
 * record, so there is nothing to type and the invitation is one click. The seller
 * has no such record to read, so it must be asked for — and a hand-typed address
 * nobody has verified goes through the send preview first. Collapsing them would
 * mean either asking the buyer surface for an address it already holds, or letting
 * an unverified one go out unreviewed. So the composer is a SLOT, and each subject
 * supplies its own.</p>
 *
 * <p>🔴 The status badge is rendered exactly as the server gives it and expiry is
 * NEVER recomputed here — see <c>inviteBits</c>.</p>
 */
export function InvitationsSection({
  subject,
  canEdit,
  rows,
  loading,
  error,
  retrying,
  onRetry,
  onResend,
  resending,
  composer,
  duplicate,
  rowContext,
  testId = "prequal-invitations",
}: {
  subject: PrequalSubject;
  canEdit: boolean;
  rows: PrequalInviteDto[];
  loading: boolean;
  error: boolean;
  retrying: boolean;
  onRetry: () => void;
  onResend: (inviteId: string) => void;
  resending: boolean;

  /**
   * The send affordance, supplied by the subject.
   *
   * <p><c>trigger</c> is the header-right control; <c>body</c> is whatever it
   * opens, rendered between the header and the table. The buyer supplies a
   * trigger and no body — one click sends. The seller supplies both.</p>
   */
  composer: { trigger?: ReactNode; body?: ReactNode };

  /** Rendered above the composer body when a live invitation already exists.
   *  The caller owns the state because only the caller knows whether the 409
   *  came from its own send. */
  duplicate?: ReactNode;

  /** Extra provenance beneath one row's address. The buyer surface names the
   *  listing an invitation was sent for. */
  rowContext?: (invite: PrequalInviteDto) => ReactNode;

  testId?: string;
}) {
  const toast = useToast();
  const copy = PREQUAL_SUBJECT_COPY[subject];

  return (
    <DetailSection
      title={copy.invitationsTitle}
      action={canEdit ? composer.trigger : null}
    >
      <div data-testid={testId} className="grid gap-space-3">
        <p className="m-0 text-caption text-gray-mid">{copy.invitationsCaption}</p>

        {composer.body}
        {duplicate}

        <InviteListState
          loading={loading}
          error={error}
          retrying={retrying}
          onRetry={onRetry}
          what={copy.invitesWhat}
        >
          {/*
            `DetailSection` is a hand-rolled panel, so it publishes no surface of
            its own and a table inside it would resolve to "page" and paint the
            screen's own full-onyx header inside a card. Declaring it here is
            what `Surface` exists for — NOT the `surface` escape-hatch prop on
            the table, which pins the answer against a container that may move.
          */}
          <Surface kind="card">
            <SubTable
              caption={copy.invitationsTitle}
              data-testid={`${testId}-table`}
              rows={rows}
              rowKey={(i) => i.id}
              rowTestId={() => `${testId}-row`}
              columns={inviteColumns({
                canEdit,
                recipientNoun: copy.noun,
                rowContext,
                resending,
                onResend,
                onCopy: (invite) => void copyInviteLink(invite, toast),
              })}
              empty={{
                message: copy.invitesEmptyTitle,
                // 🔴 Prose, never a control. An invitation is sent from the
                // header above — a button here would be a second door to one
                // act, and a "derived" door with no `onClick` is the vocabulary
                // for exactly that: name what will fill this, offer nothing that
                // cannot do it.
                door: { kind: "derived", explain: copy.invitesEmptyDescription },
              }}
            />
          </Surface>
        </InviteListState>
      </div>
    </DetailSection>
  );
}

/**
 * The one-click send trigger, for a subject whose address is already on file.
 *
 * <p>Exported so the buyer surface composes it rather than hand-rolling a
 * button that has to remember the pending label.</p>
 *
 * <p><b>The section's PRIMARY weight, stated rather than inherited.</b> Sending
 * is the one act the invitations section exists to enable, so it is the one
 * primary control on it — but it read as primary only because the variant prop
 * was omitted and the component's default happened to agree. An accident that
 * lands on the right answer is still an accident: the next author reading four
 * buttons with three different implied weights has no way to tell which were
 * decided.</p>
 */
export function OneClickInviteTrigger({
  subject,
  pending,
  onSend,
}: {
  subject: PrequalSubject;
  pending: boolean;
  onSend: () => void;
}) {
  return (
    <Button variant="primary" size="sm" onClick={onSend} disabled={pending}>
      <Send className="h-4 w-4" aria-hidden />
      {pending ? "Sending…" : PREQUAL_SUBJECT_COPY[subject].inviteAction}
    </Button>
  );
}

/** The duplicate confirm, pre-wired to the subject's own wording. */
export function SubjectDuplicateConfirm({
  subject,
  testId,
  pending,
  onConfirm,
  onCancel,
}: {
  subject: PrequalSubject;
  testId: string;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <DuplicateInviteConfirm
      testId={testId}
      subject={PREQUAL_SUBJECT_COPY[subject].duplicateSubject}
      pending={pending}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
